import React, {useEffect, useState} from 'react';
import './PartnerRegistrationsPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import Pagination from '../../elements/Pagination/Pagination';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {getPartnerRewardDisplayProps} from '../../../utils/partner/getPartnerRewardDisplayProps';
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";

const PAGE_ID = 'PartnerRegistrationsPage';
const PAGE_SIZE = 10;

function formatDate(isoString) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}, ${date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'})}`;
}

/**
 * История регистраций по реферальной ссылке партнера: пагинационный список
 * пользователей и наград, начисленных им за регистрацию. Для админа —
 * по userId партнера, для самого партнера (isSelfView) — по своей учетке.
 */
function PartnerRegistrationsPage({
  targetUserId, targetUserName, isSelfView = false, onBack, onUserClick,
}) {
  usePageAnalytics('PartnerRegistrationsPage');

  const [registrations, setRegistrations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cache, setCache] = useState(new Map());

  useEffect(() => {
    const fetchRegistrations = async () => {
      setLoading(true);
      setError(null);

      const cacheKey = `${isSelfView ? 'self' : targetUserId}_${page}`;
      const cached = cache.get(cacheKey);
      if (cached) {
        setRegistrations(cached.registrations);
        setTotalPages(cached.totalPages);
        setTotal(cached.total);
        setLoading(false);
        return;
      }

      try {
        const url = isSelfView
          ? `${API_BASE}/partner/my-registrations?limit=${PAGE_SIZE}&page=${page}`
          : `${API_BASE}/admin/partner-registrations/${targetUserId}?limit=${PAGE_SIZE}&page=${page}`;
        const res = await fetch(url, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Не удалось загрузить историю регистраций');
        }
        setRegistrations(data.registrations || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, data);
          return next;
        });
      } catch (e) {
        console.error('Partner registrations error', e);
        setError(e.message || 'Не удалось загрузить историю регистраций');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, [page, cache, targetUserId, isSelfView]);

  const handlePageChange = (newPage) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setPage(newPage);
  };

  return (
    <>
      <h1 className="admin-user-profile-title">История регистраций</h1>
      {targetUserName && <h1 className="admin-user-profile-name">{targetUserName}</h1>}

      {loading && <p className="partner-registrations-status">Загрузка...</p>}
      {error && <p className="partner-registrations-status partner-registrations-status_error">{error}</p>}

      {!loading && !error && (
        <>
          {registrations.length === 0 ? (
            <p className="partner-registrations-status">
              По реферальной ссылке пока никто не зарегистрировался
            </p>
          ) : (
            <>
              <p className="partner-registrations-total">
                Всего регистраций: <span className="text-coins">{total}</span>
              </p>
              <div className="partner-registrations-list">
                {registrations.map((registration, index) => (
                  // Плашка кликабельна и ведет в профиль игрока — как строка
                  // игрока в Зале славы (PageButton, чтобы работали клавиатура,
                  // звук и аналитика). Внутри только строчные элементы: блочные
                  // в кнопку вкладывать нельзя
                  <PageButton
                    key={registration.userId}
                    className="partner-registration-item"
                    pageId={PAGE_ID}
                    buttonId={`registration_${index}`}
                    onClick={() => onUserClick?.(registration.userId, registration.userName)}
                  >
                    <span className="partner-registration-item-header">
                      <span className="partner-registration-item-name">{registration.userName}</span>
                      <span className="partner-registration-item-date">{formatDate(registration.createdAt)}</span>
                    </span>
                    <span className="partner-registration-item-rewards">
                      {registration.hasReceivedPartnerReferalRewards && registration.rewards.length > 0 ? (
                        registration.rewards.map((reward, index) => {
                          const display = getPartnerRewardDisplayProps(reward);
                          return (
                            <span key={index} className={`partner-registration-reward ${display.colorClass}`}>
                              {display.fullLabel}
                              {display.iconSrc && (
                                <img
                                  className="partner-registration-reward-icon"
                                  src={display.iconSrc}
                                  alt=""
                                />
                              )}
                            </span>
                          );
                        })
                      ) : (
                        <span className="partner-registration-reward partner-registration-reward_pending">
                          Награды еще не получены
                        </span>
                      )}
                    </span>
                  </PageButton>
                ))}
              </div>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange}/>
            </>
          )}
        </>
      )}

      <div className="main-menu-buttons partner-registrations-page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default PartnerRegistrationsPage;
